import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowRight, Loader2, Zap, AlertTriangle, Bell, Clock, 
  CheckCircle, Calendar, Phone, DollarSign
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("tinkah_token")}` }
});

const statusConfig = {
  pending: { label: "Pending", color: "bg-[#D6AD60]/20 text-[#5C4D24] border-[#D6AD60]", icon: Clock },
  in_progress: { label: "In Progress", color: "bg-[#9F7D23]/20 text-[#5C4D24] border-[#9F7D23]", icon: Loader2 },
  completed: { label: "Completed", color: "bg-[#4B6F44]/20 text-[#4B6F44] border-[#4B6F44]", icon: CheckCircle },
};

export default function SalesDashboard() {
  const [orders, setOrders] = useState([]);
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dragging, setDragging] = useState(null);
  const [alertsExpanded, setAlertsExpanded] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [ordersRes, alertsRes] = await Promise.all([
        axios.get(`${API}/orders`, getAuthHeaders()),
        axios.get(`${API}/alerts`, getAuthHeaders()),
      ]);
      setOrders(ordersRes.data);
      setAlerts(alertsRes.data);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, order) => {
    setDragging(order);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    if (!dragging || dragging.status === newStatus) {
      setDragging(null);
      return;
    }
    try {
      await axios.put(`${API}/orders/${dragging.id}/status`, { status: newStatus }, getAuthHeaders());
      setOrders(orders.map(o => o.id === dragging.id ? { ...o, status: newStatus } : o));
      toast.success(`Order moved to ${statusConfig[newStatus].label}`);
    } catch (err) {
      toast.error("Failed to update order status");
    }
    setDragging(null);
  };

  const getOrdersByStatus = (status) => orders.filter(o => o.status === status);

  const totalAlerts = alerts ? alerts.overdue_count + alerts.upcoming_count + alerts.no_followup_count : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 text-[#9F7D23] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sales-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h2 className="font-['Space_Mono'] text-3xl md:text-4xl tracking-tight uppercase text-[#5C4D24]">
            Pipeline
          </h2>
          <p className="text-sm text-[#8B7536] mt-1">Drag orders to change status</p>
        </div>
        <Link
          to="/orders/new"
          data-testid="new-order-btn"
          className="bg-[#9F7D23] text-white hover:bg-[#8A6D1F] px-6 md:px-8 py-3 font-['Space_Mono'] tracking-widest uppercase text-xs transition-all hover:translate-y-[-1px] active:scale-95 flex items-center gap-2"
        >
          New Order <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Alerts Section */}
      {totalAlerts > 0 && (
        <div className="bg-white border border-[#D6AD60] p-4 animate-fade-in-up" data-testid="alerts-section">
          <button 
            onClick={() => setAlertsExpanded(!alertsExpanded)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-[#9F7D23]" />
              <span className="font-['Space_Mono'] text-sm text-[#5C4D24]">
                {totalAlerts} Payment Follow-up{totalAlerts > 1 ? "s" : ""} Required
              </span>
            </div>
            <span className="text-xs text-[#8B7536]">{alertsExpanded ? "Hide" : "Show"}</span>
          </button>
          
          {alertsExpanded && (
            <div className="mt-4 space-y-4 border-t border-[#D6D6CF] pt-4">
              {/* Overdue */}
              {alerts.overdue?.length > 0 && (
                <div>
                  <h4 className="font-['Space_Mono'] text-xs tracking-widest uppercase text-[#9F3A3A] mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> Overdue ({alerts.overdue.length})
                  </h4>
                  <div className="space-y-2">
                    {alerts.overdue.map((order) => (
                      <Link 
                        key={order.id} 
                        to={`/orders/${order.id}`}
                        className="flex items-center justify-between p-3 bg-[#9F3A3A]/5 border border-[#9F3A3A]/20 hover:bg-[#9F3A3A]/10 transition-colors"
                      >
                        <div>
                          <span className="font-['Space_Mono'] text-sm text-[#9F7D23]">{order.order_number}</span>
                          <span className="mx-2 text-[#8B7536]">•</span>
                          <span className="text-sm text-[#5C4D24]">{order.customer?.name}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-[#9F3A3A] font-bold">{order.financial?.balance_due} AED due</span>
                          <Phone className="w-4 h-4 text-[#8B7536]" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Upcoming */}
              {alerts.upcoming?.length > 0 && (
                <div>
                  <h4 className="font-['Space_Mono'] text-xs tracking-widest uppercase text-[#D6AD60] mb-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4" /> Upcoming This Week ({alerts.upcoming.length})
                  </h4>
                  <div className="space-y-2">
                    {alerts.upcoming.map((order) => (
                      <Link 
                        key={order.id} 
                        to={`/orders/${order.id}`}
                        className="flex items-center justify-between p-3 bg-[#D6AD60]/5 border border-[#D6AD60]/20 hover:bg-[#D6AD60]/10 transition-colors"
                      >
                        <div>
                          <span className="font-['Space_Mono'] text-sm text-[#9F7D23]">{order.order_number}</span>
                          <span className="mx-2 text-[#8B7536]">•</span>
                          <span className="text-sm text-[#5C4D24]">{order.customer?.name}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-[#8B7536]">Follow up: {order.financial?.follow_up_date}</span>
                          <span className="text-sm text-[#D6AD60] font-bold">{order.financial?.balance_due} AED</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* No Follow-up Set */}
              {alerts.no_followup?.length > 0 && (
                <div>
                  <h4 className="font-['Space_Mono'] text-xs tracking-widest uppercase text-[#8B7536] mb-2 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" /> No Follow-up Date Set ({alerts.no_followup.length})
                  </h4>
                  <div className="space-y-2">
                    {alerts.no_followup.slice(0, 5).map((order) => (
                      <Link 
                        key={order.id} 
                        to={`/orders/${order.id}`}
                        className="flex items-center justify-between p-3 bg-[#F5F5F0] border border-[#D6D6CF] hover:bg-[#F5F5F0]/50 transition-colors"
                      >
                        <div>
                          <span className="font-['Space_Mono'] text-sm text-[#9F7D23]">{order.order_number}</span>
                          <span className="mx-2 text-[#8B7536]">•</span>
                          <span className="text-sm text-[#5C4D24]">{order.customer?.name}</span>
                        </div>
                        <span className="text-sm text-[#5C4D24]">{order.financial?.balance_due} AED pending</span>
                      </Link>
                    ))}
                    {alerts.no_followup.length > 5 && (
                      <p className="text-xs text-[#8B7536] text-center">+ {alerts.no_followup.length - 5} more</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Kanban Board */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-fade-in-up">
        {Object.entries(statusConfig).map(([status, config]) => {
          const StatusIcon = config.icon;
          const statusOrders = getOrdersByStatus(status);
          
          return (
            <div
              key={status}
              className={`bg-white border border-[#D6D6CF] min-h-[400px] transition-all ${
                dragging && dragging.status !== status ? "border-dashed border-[#9F7D23]" : ""
              }`}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, status)}
              data-testid={`kanban-column-${status}`}
            >
              <div className="p-4 border-b border-[#D6D6CF] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <StatusIcon className={`w-4 h-4 ${status === "completed" ? "text-[#4B6F44]" : "text-[#9F7D23]"}`} />
                  <h3 className="font-['Space_Mono'] text-sm tracking-tight uppercase text-[#5C4D24]">
                    {config.label}
                  </h3>
                </div>
                <span className="font-['Space_Mono'] text-xs text-[#8B7536]">{statusOrders.length}</span>
              </div>
              
              <div className="p-3 space-y-3">
                {statusOrders.map((order) => (
                  <div
                    key={order.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, order)}
                    className={`p-4 border border-[#D6D6CF] bg-[#FAFAF8] cursor-move hover:border-[#9F7D23] transition-all ${
                      dragging?.id === order.id ? "opacity-50" : ""
                    }`}
                    data-testid={`kanban-card-${order.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <Link 
                        to={`/orders/${order.id}`}
                        className="font-['Space_Mono'] text-sm text-[#9F7D23] hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {order.order_number}
                      </Link>
                      {order.financial?.is_urgent && (
                        <Zap className="w-4 h-4 text-[#9F3A3A]" />
                      )}
                    </div>
                    <p className="text-sm text-[#5C4D24] mb-1">{order.customer?.name}</p>
                    {order.customer?.profile_name && (
                      <p className="text-xs text-[#9F7D23] mb-2">For: {order.customer.profile_name}</p>
                    )}
                    <div className="flex items-center justify-between text-xs text-[#8B7536]">
                      <span>{order.items?.length || 0} item{order.items?.length !== 1 ? "s" : ""}</span>
                      <span className="font-['Space_Mono']">{order.financial?.total_price} AED</span>
                    </div>
                    {order.financial?.balance_due > 0 ? (
                      <div className="mt-2 pt-2 border-t border-[#D6D6CF] flex items-center justify-between">
                        <span className="text-xs text-[#9F3A3A]">Balance: {order.financial.balance_due} AED</span>
                        {order.financial?.follow_up_date && (
                          <span className="text-[10px] text-[#8B7536]">{order.financial.follow_up_date}</span>
                        )}
                      </div>
                    ) : (
                      <div className="mt-2 pt-2 border-t border-[#D6D6CF]">
                        <span className="text-xs font-['Space_Mono'] px-2 py-0.5 bg-[#4B6F44] text-white uppercase tracking-widest">Fully Paid</span>
                      </div>
                    )}
                  </div>
                ))}
                
                {statusOrders.length === 0 && (
                  <div className="py-8 text-center text-sm text-[#8B7536]">
                    {status === "pending" ? "No pending orders" : 
                     status === "in_progress" ? "No orders in progress" : 
                     "No completed orders"}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
